import React from "react";
import { Box, Card, Divider, Stack, SxProps, Typography } from "@mui/material";
import { sharedStyles } from "./cardStyles";

import { IProduct } from "hooks/translations.interfaces";
import { useTranslations } from "hooks/useTranslations";
import {
  IBooking,
  IBookingPassenger,
  IFareBreakdown,
  IPassengerProduct,
  IPaymentDetails,
  MediaQuery,
  formatPrice,
} from "@thekilroygroup/shared-ui";

interface IAggregatedProduct {
  productCode: string;
  totalPrice: number;
  count: number;
}

export interface IConfirmation extends IBooking {
  bookingProducts: IPassengerProduct[];
}

interface IProps {
  children: any;
  confirmation: IConfirmation;
}
export const PriceSummaryCard = ({ children, confirmation }: IProps) => {
  const translations = useTranslations().translations;
  const { paymentDetails } = translations.confirmationPage;
  const priceTranslations = translations.priceSummary;
  const locale = translations.locale;

  const {
    passengers,
    payment,
    itinerary,
    selectedPaymentMethod,
    bookingProducts = [],
  } = confirmation;
  const discount = selectedPaymentMethod.discount.amount;

  const getPrice = (
    fareBreakdown: IFareBreakdown,
    payment: IPaymentDetails,
  ) => {
    const quantity = fareBreakdown.passengerQuantity;
    const total = fareBreakdown.fareAmounts.total;
    const fee = payment.fee;
    const count = itinerary.fareBreakdowns.reduce(
      (accumulator: number, fb: IFareBreakdown) => {
        return accumulator + fb.passengerQuantity;
      },
      0,
    );
    return quantity * (total + fee / count);
  };

  const getPassengerTypeLabel = (fare: IFareBreakdown): string | undefined => {
    const passengerTypes = translations.passengerTypes;
    const translatedPassengerType =
      passengerTypes[fare.passengerType as keyof typeof passengerTypes];
    return `${translatedPassengerType} x ${fare.passengerQuantity}`;
  };

  const getProductLines = (passengers: IBookingPassenger[]) => {
    const passengerProductLines = passengers.reduce(
      (result: any, entry: IBookingPassenger) => {
        if (entry.passengerProducts) {
          entry.passengerProducts.forEach((product: IPassengerProduct) => {
            if (product.productCode) {
              if (!result[product.productCode]) {
                result[product.productCode] = {
                  productCode: product.productCode,
                  totalPrice: 0,
                  count: 0,
                };
              }
              result[product.productCode].totalPrice += product.price;
              result[product.productCode].count++;
            }
          });
        }
        return result;
      },
      {},
    );

    let bookingProductsLines: { [key: string]: IAggregatedProduct } = {};
    bookingProducts?.forEach((product: IPassengerProduct) => {
      bookingProductsLines[product.productCode] = {
        productCode: product.productCode,
        totalPrice: product.price,
        count: -1,
      };
    });

    return Object.values({
      ...passengerProductLines,
      ...bookingProductsLines,
    }) as IAggregatedProduct[];
  };

  const getProductTitle = (productCode: string) => {
    const product: IProduct | undefined =
      translations.dynamicContent.products.find(
        (product: IProduct) => product.productCode === productCode,
      );
    return product?.title || "";
  };

  const getSubtotalPrice = (
    paymentPrice: number,
    discount: number,
    passengers: IBookingPassenger[],
  ) => {
    let productPrices = 0;
    let cardPrices = 0;
    passengers?.forEach((passenger: IBookingPassenger) => {
      passenger.passengerProducts?.forEach((product: IPassengerProduct) => {
        productPrices += product.price;
      });
      if (passenger.cardRequest) {
        cardPrices += passenger.cardRequest.cardPrice;
      }
    });
    return paymentPrice + productPrices + discount + cardPrices;
  };

  return (
    <>
      <MediaQuery min="md">{children}</MediaQuery>
      <Card sx={sharedStyles.card}>
        <MediaQuery max="md">{children}</MediaQuery>
        <Typography sx={sharedStyles.cardHeader} variant="h5">
          {paymentDetails.price}
        </Typography>
        {itinerary.fareBreakdowns.map(
          (fareBreakdown: IFareBreakdown, i: number) => {
            return (
              <Stack sx={sharedStyles.wrapper} key={`ticket-line-${i}`}>
                <Typography
                  sx={
                    {
                      ...sharedStyles.label,
                      ...sharedStyles.paymentLabel,
                    } as SxProps
                  }
                >
                  {getPassengerTypeLabel(fareBreakdown)}
                </Typography>
                <Typography
                  sx={
                    {
                      ...sharedStyles.valueAlignRight,
                      ...sharedStyles.paymentValue,
                    } as SxProps
                  }
                  variant="titleMedium"
                  weight="bold"
                >
                  {formatPrice(
                    getPrice(fareBreakdown, payment),
                    locale,
                    payment.currency,
                    false,
                  )}
                </Typography>
              </Stack>
            );
          },
        )}

        {/* Tax */}
        <Stack sx={sharedStyles.wrapper}>
          <Typography
            sx={
              { ...sharedStyles.label, ...sharedStyles.paymentLabel } as SxProps
            }
            variant="bodySmall"
          >
            {priceTranslations.tax}
          </Typography>
          <Typography
            sx={
              {
                ...sharedStyles.valueAlignRight,
                ...sharedStyles.paymentValue,
              } as SxProps
            }
            variant="bodySmall"
          >
            ({formatPrice(payment.tax, locale, payment.currency, false)})
          </Typography>
        </Stack>

        {/* CARDS  */}
        {passengers.some(
          (passenger: IBookingPassenger) => passenger.cardRequest,
        ) && (
          <>
            <Divider variant="fullWidth" sx={{ my: 2 }} />

            {passengers.map((passenger: IBookingPassenger, i: number) => {
              return (
                <Box key={`card-${i}`}>
                  {passenger.cardRequest && (
                    <Stack sx={sharedStyles.wrapper}>
                      <Typography
                        sx={
                          {
                            ...sharedStyles.label,
                            ...sharedStyles.paymentLabel,
                          } as SxProps
                        }
                      >
                        {passenger.cardRequest.cardType}
                      </Typography>
                      <Typography
                        sx={
                          {
                            ...sharedStyles.valueAlignRight,
                            ...sharedStyles.paymentValue,
                          } as SxProps
                        }
                        variant="titleMedium"
                        weight="bold"
                      >
                        {formatPrice(
                          passenger.cardRequest.cardPrice,
                          locale,
                          payment.currency,
                          false,
                        )}
                      </Typography>
                    </Stack>
                  )}
                </Box>
              );
            })}
          </>
        )}

        {getProductLines(passengers).map(
          (product: IAggregatedProduct, i: number) => {
            return (
              <Box key={`product-${i}`}>
                {i === 0 && <Divider variant="fullWidth" sx={{ my: 2 }} />}
                <Stack sx={sharedStyles.wrapper}>
                  <Typography
                    sx={
                      {
                        ...sharedStyles.label,
                        ...sharedStyles.paymentLabel,
                      } as SxProps
                    }
                  >
                    {getProductTitle(product.productCode)}{" "}
                    {product.count !== -1 ? `x ${product.count}` : ""}
                  </Typography>
                  <Typography
                    sx={
                      {
                        ...sharedStyles.valueAlignRight,
                        ...sharedStyles.paymentValue,
                      } as SxProps
                    }
                    variant="titleMedium"
                    weight="bold"
                  >
                    {formatPrice(
                      product.totalPrice,
                      locale,
                      payment.currency,
                      false,
                    )}
                  </Typography>
                </Stack>
              </Box>
            );
          },
        )}
        <Divider variant="fullWidth" sx={{ my: 2 }} />
        <Stack sx={sharedStyles.wrapper}>
          <Typography
            sx={
              { ...sharedStyles.label, ...sharedStyles.paymentLabel } as SxProps
            }
          >
            {priceTranslations.subTotal}
          </Typography>
          <Typography
            sx={
              {
                ...sharedStyles.valueAlignRight,
                ...sharedStyles.paymentValue,
              } as SxProps
            }
          >
            {formatPrice(
              getSubtotalPrice(payment.amount, discount, passengers),
              locale,
              payment.currency,
              false,
            )}
          </Typography>
        </Stack>
        {discount !== 0 && (
          <Stack sx={sharedStyles.wrapper}>
            <Typography
              sx={
                {
                  ...sharedStyles.label,
                  ...sharedStyles.paymentLabel,
                } as SxProps
              }
            >
              {priceTranslations.discount}
            </Typography>
            <Typography
              sx={
                {
                  ...sharedStyles.valueAlignRight,
                  ...sharedStyles.paymentValue,
                } as SxProps
              }
            >
              {formatPrice(-1 * discount, locale, payment.currency, false)}
            </Typography>
          </Stack>
        )}
        <Divider variant="fullWidth" color={"black.main"} sx={{ my: 2 }} />
        <Stack
          sx={
            {
              ...sharedStyles.wrapper,
              backgroundColor: "grey.200",
              py: 0.25,
              px: 1,
              mx: -1,
            } as SxProps
          }
        >
          <Typography
            sx={
              {
                ...sharedStyles.label,
                ...sharedStyles.paymentLabel,
              } as SxProps
            }
          >
            {priceTranslations.total}
          </Typography>
          <Typography
            variant="titleLarge"
            weight="bold"
            sx={
              {
                ...sharedStyles.valueAlignRight,
                ...sharedStyles.paymentValue,
                m: "auto",
              } as SxProps
            }
          >
            {formatPrice(payment.totalPaid, locale, payment.currency, false)}
          </Typography>
        </Stack>
      </Card>
    </>
  );
};
